import classNames from "classnames";
import { MobileFiltersContainer } from "./MobileFilters.styled";
import { LabelNavigation } from "src/molecules/LabelNavigation";
import { ArrowLeft } from "./DrawerFilter.styled";
import { useLockBodyScroll } from "src/hooks/useLockBodyScroll";
import { MobileFilterOption } from "./MobileFilterOption";
import { Trans } from "@lingui/macro";
import { Button } from "src/atoms/Button";
import { useCallback, useMemo } from "react";
import { useRemoveFilters } from "src/hooks/use-handle-search-params";
import { useFacetContext } from "src/organisms/ProductList/components/Facets/facet.context";
import { useListingState } from "src/hooks/stores/use-listing-store";
import type {
  ListingFacet,
  ListingFacetKeys,
} from "src/types/adapters/listing.type";

type MobileFiltersProps = {
  isOpen: boolean;
  toggleMobileFilters: () => void;
};

type AvailableFacet = {
  key: ListingFacetKeys;
  facet: ListingFacet;
};

export const EXCLUDED_MOBILE_FACETS: readonly ListingFacetKeys[] = [
  "category",
] as const;

function MobileFilters({
  isOpen,
  toggleMobileFilters,
}: MobileFiltersProps): JSX.Element {
  useLockBodyScroll(isOpen);
  const { selectedFacets, trackFilterReset, handleScrollToTop } =
    useFacetContext();
  const { facets, appliedFacets, totalResults: total } = useListingState();
  const { removeAllFilters } = useRemoveFilters();

  const hasFilters = useMemo(
    () =>
      Object.entries(appliedFacets).some(
        ([key, arr]) =>
          !EXCLUDED_MOBILE_FACETS.includes(key as ListingFacetKeys) &&
          (arr?.length ?? 0) > 0
      ),
    [appliedFacets]
  );

  const availableFacets = useMemo<AvailableFacet[]>(() => {
    if (!facets) {
      return [];
    }

    return selectedFacets.flatMap((key) => {
      const facet = facets.get(key);

      if (!facet || EXCLUDED_MOBILE_FACETS.includes(key)) {
        return [];
      }

      return [{ key, facet }];
    });
  }, [facets, selectedFacets]);

  const handleClearAll = useCallback(() => {
    removeAllFilters(false);
    trackFilterReset();
    handleScrollToTop();
  }, [removeAllFilters, trackFilterReset, handleScrollToTop]);

  return (
    <MobileFiltersContainer className={classNames({ ["open"]: isOpen })}>
      <LabelNavigation
        label={{ id: "Filters" }}
        leftAdornment={<ArrowLeft strokeWidth="2" />}
        onClick={toggleMobileFilters}
      />
      <div className="content-wrapper">
        {availableFacets.map(({ key, facet }) => (
          <MobileFilterOption key={key} facet={facet} />
        ))}
      </div>

      <div className="close-button">
        {hasFilters ? (
          <Button fullWidth variant="tertiary" onClick={handleClearAll}>
            <Trans>Clear all</Trans>
          </Button>
        ) : null}

        <Button fullWidth variant="primary" onClick={toggleMobileFilters}>
          {hasFilters ? (
            <>
              <Trans>Show {total} results</Trans>
            </>
          ) : (
            <Trans>Close</Trans>
          )}
        </Button>
      </div>
    </MobileFiltersContainer>
  );
}

export { MobileFilters };
